import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible/Collapsible";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { format } from "date-fns";
import { BookOpen, Eye, EyeClosed } from "lucide-react";
import { useState } from "react";

const UpcomingExplorations = ({
  data,
  loading,
}: {
  data: {
    explorations: any[];
    rotations: any[];
    sessions: any[];
    teachers: any[];
  };
  loading: boolean;
}) => {
  const [open, setOpen] = useState<boolean>(true);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      style={{ marginTop: "-8px" }}
    >
      <VStack gap="2rem">
        <VStack gap={0}>
          <HStack>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                lineHeight: "var(--line-height-lg)",
                fontWeight: "var(--font-bold)",
              }}
            >
              Upcoming Explorations
            </h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon-small">
                    {open ? <EyeClosed /> : <Eye />}
                  </Button>
                </CollapsibleTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {open ? "Hide" : "Show"} upcoming explorations section.
              </TooltipContent>
            </Tooltip>
          </HStack>
          <p style={{ color: "var(--muted-foreground)" }}>
            Your explorations within the next week
          </p>
        </VStack>
        <CollapsibleContent>
          {loading ? (
            <HStack gap="1rem">
              <Skeleton style={{ height: "320px", width: "100%" }} />
              <Skeleton style={{ height: "320px", width: "100%" }} />
              <Skeleton style={{ height: "320px", width: "100%" }} />
              <Skeleton style={{ height: "320px", width: "100%" }} />
            </HStack>
          ) : data.explorations.length < 1 ? (
            <Empty style={{ borderColor: "var(--secondary-border)" }}>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <BookOpen />
                </EmptyMedia>
                <EmptyTitle>No Upcoming Explorations Found</EmptyTitle>
                <EmptyDescription>
                  There were no explorations found for the next week. If you
                  believe this is an error, please refresh.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <HStack gap="1rem" style={{ flexWrap: "wrap" }}>
              {data.explorations.map((e) => {
                const session = data.sessions.find((s) => s._id === e.session);
                const rotation = data.rotations.find(
                  (r) => r._id === session.rotation,
                );

                return (
                  <DashboardCard
                    key={e._id}
                    style={{
                      flex: "1 1 calc(20% - 1rem)",
                      flexWrap: "nowrap",
                    }}
                  >
                    <VStack
                      justify="space-between"
                      gap="1rem"
                      style={{ height: "100%" }}
                    >
                      <VStack gap="1rem">
                        <VStack gap="0.25rem">
                          <p
                            style={{
                              fontSize: "var(--text-md)",
                              lineHeight: "var(--line-height-md)",
                              fontWeight: "var(--font-bold)",
                            }}
                          >
                            {e.name}
                          </p>
                          <p
                            style={{
                              fontSize: "var(--text-xs)",
                              lineHeight: "var(--line-height-xs)",
                              color: "var(--muted-foreground)",
                            }}
                          >
                            {e.description}
                          </p>
                        </VStack>
                        <VStack gap="0.125rem">
                          <p>Date: {format(rotation.date, "MMMM dd, yyyy")}</p>
                          <p>Session: {session.name}</p>
                          {data.teachers.find((t) => t._id === e.teacher) && (
                            <p>
                              Teacher:{" "}
                              {
                                data.teachers.find((t) => t._id === e.teacher)
                                  .name
                              }
                            </p>
                          )}
                          {e.coTeachers.length > 0 && (
                            <p>
                              Co-Teachers:{" "}
                              {e.coTeachers
                                .map(
                                  (c: string) =>
                                    data.teachers.find((t) => t._id === c).name,
                                )
                                .join(", ")}
                            </p>
                          )}
                          <p>Grade Levels: {e.gradeLevels.join(", ")}</p>
                          <p>Pathway: {e.pathway}</p>
                          <p>Room: {e.room}</p>
                        </VStack>
                      </VStack>
                    </VStack>
                  </DashboardCard>
                );
              })}
            </HStack>
          )}
        </CollapsibleContent>
      </VStack>
    </Collapsible>
  );
};

export { UpcomingExplorations };
